/**
 * Perspective rectification ported from the ~/dev/dgbiotech/progetto1 "ScanRetto" prototype.
 * Pure geometry: solves the homography that maps the user-placed quadrilateral onto an
 * axis-aligned rectangle, then resamples the source with bilinear interpolation. No pixel is
 * invented — only a geometric transform. Works on `ImageData` alone, so it runs unchanged on the
 * page and in the engine's Worker. Written to satisfy `noUncheckedIndexedAccess`: the four corners
 * are a fixed tuple, and the flat numeric buffers are read through total accessors.
 */
export interface Point {
  x: number;
  y: number;
}

/** Corner order: top-left, top-right, bottom-right, bottom-left. */
export type Corners = readonly [Point, Point, Point, Point];

export const MAX_OUTPUT_LONG_EDGE = 2200;

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Output pixel size derived from the average side lengths of the quad, capped on the long edge. */
export function outputSize(
  corners: Corners,
  maxLongEdge = MAX_OUTPUT_LONG_EDGE,
): { readonly w: number; readonly h: number } {
  const [tl, tr, br, bl] = corners;
  const width = Math.max(distance(tl, tr), distance(bl, br));
  const height = Math.max(distance(tl, bl), distance(tr, br));
  const scale = Math.min(1, maxLongEdge / Math.max(width, height));
  return {
    w: Math.max(8, Math.round(width * scale)),
    h: Math.max(8, Math.round(height * scale)),
  };
}

/** Default corners: inset 6% from each edge, a sensible starting quad for the user to adjust. */
export function defaultCorners(width: number, height: number): Corners {
  const m = 0.06;
  return [
    { x: width * m, y: height * m },
    { x: width * (1 - m), y: height * m },
    { x: width * (1 - m), y: height * (1 - m) },
    { x: width * m, y: height * (1 - m) },
  ];
}

/** The same quad in a copy of the image scaled by `factor` (the engine's preview copy). */
export function scaleCorners(corners: Corners, factor: number): Corners {
  const s = (p: Point): Point => ({ x: p.x * factor, y: p.y * factor });
  return [s(corners[0]), s(corners[1]), s(corners[2]), s(corners[3])];
}

/**
 * Solves H (output -> source) for the four point correspondences via Gauss elimination on the
 * 8×9 augmented matrix, returning the 9 coefficients (h33 fixed at 1).
 */
function solveHomography(dst: Corners, src: Corners): readonly number[] {
  const a: number[][] = [];
  for (let i = 0; i < 4; i++) {
    const d = dst[i] ?? { x: 0, y: 0 };
    const s = src[i] ?? { x: 0, y: 0 };
    a.push([d.x, d.y, 1, 0, 0, 0, -s.x * d.x, -s.x * d.y, s.x]);
    a.push([0, 0, 0, d.x, d.y, 1, -s.y * d.x, -s.y * d.y, s.y]);
  }

  const get = (r: number, c: number): number => a[r]?.[c] ?? 0;
  const set = (r: number, c: number, value: number): void => {
    const row = a[r];
    if (row) row[c] = value;
  };

  for (let c = 0; c < 8; c++) {
    let pivot = c;
    for (let r = c + 1; r < 8; r++) {
      if (Math.abs(get(r, c)) > Math.abs(get(pivot, c))) pivot = r;
    }
    const rowC = a[c];
    const rowP = a[pivot];
    if (rowC && rowP) {
      a[c] = rowP;
      a[pivot] = rowC;
    }
    const pivotValue = get(c, c) || 1e-12;
    for (let j = c; j < 9; j++) set(c, j, get(c, j) / pivotValue);
    for (let r = 0; r < 8; r++) {
      if (r === c) continue;
      const factor = get(r, c);
      for (let j = c; j < 9; j++) set(r, j, get(r, j) - factor * get(c, j));
    }
  }

  return [
    get(0, 8),
    get(1, 8),
    get(2, 8),
    get(3, 8),
    get(4, 8),
    get(5, 8),
    get(6, 8),
    get(7, 8),
    1,
  ];
}

/** Writes the bilinear sample of `source` at (u, v) as one RGBA pixel at `k`; returns the next `k`. */
function sampleInto(op: Uint8ClampedArray, start: number, source: ImageData, u: number, v: number) {
  let k = start;
  const sw = source.width;
  const src = source.data;
  const x0 = Math.floor(u);
  const y0 = Math.floor(v);
  if (x0 < 0 || y0 < 0 || x0 + 1 >= sw || y0 + 1 >= source.height) {
    // Resolution 9 (SL-0027): a raw ImageData byte write operates on decoded raster pixel
    // data and cannot read a live CSS custom property — this white out-of-bounds fill is a
    // documented, accepted design-system exception, not an unported token.
    op[k++] = 255;
    op[k++] = 255;
    op[k++] = 255;
    op[k++] = 255;
    return k;
  }
  const fx = u - x0;
  const fy = v - y0;
  const i00 = (y0 * sw + x0) * 4;
  const i10 = i00 + 4;
  const i01 = ((y0 + 1) * sw + x0) * 4;
  const i11 = i01 + 4;
  for (let c = 0; c < 3; c++) {
    const p00 = src[i00 + c] ?? 255;
    const p10 = src[i10 + c] ?? 255;
    const p01 = src[i01 + c] ?? 255;
    const p11 = src[i11 + c] ?? 255;
    op[k++] = p00 * (1 - fx) * (1 - fy) + p10 * fx * (1 - fy) + p01 * (1 - fx) * fy + p11 * fx * fy;
  }
  op[k++] = 255;
  return k;
}

/**
 * Rectifies `source` into new pixels using the quadrilateral `corners` (in source-pixel
 * coordinates): the deskewed, cropped document, its long edge at most `maxLongEdge`.
 */
export function warpPixels(
  source: ImageData,
  corners: Corners,
  maxLongEdge = MAX_OUTPUT_LONG_EDGE,
): ImageData {
  const { w, h } = outputSize(corners, maxLongEdge);
  const dstCorners: Corners = [
    { x: 0, y: 0 },
    { x: w, y: 0 },
    { x: w, y: h },
    { x: 0, y: h },
  ];
  const homography = solveHomography(dstCorners, corners);
  const h0 = homography[0] ?? 0;
  const h1 = homography[1] ?? 0;
  const h2 = homography[2] ?? 0;
  const h3 = homography[3] ?? 0;
  const h4 = homography[4] ?? 0;
  const h5 = homography[5] ?? 0;
  const h6 = homography[6] ?? 0;
  const h7 = homography[7] ?? 0;

  const out = new ImageData(w, h);
  const op = out.data;
  let k = 0;
  for (let y = 0; y < h; y++) {
    const yy = y + 0.5;
    for (let x = 0; x < w; x++) {
      const xx = x + 0.5;
      const den = h6 * xx + h7 * yy + 1;
      const u = (h0 * xx + h1 * yy + h2) / den - 0.5;
      const v = (h3 * xx + h4 * yy + h5) / den - 0.5;
      k = sampleInto(op, k, source, u, v);
    }
  }

  return out;
}
